import React, { useState, useEffect, useMemo } from "react";
import classnames from "classnames";
import { FeatureListing } from "../../StarterTrial/FeatureListing";
import {
    PlanPrice,
    PlanPrivacy,
    PlanFrequencySelector
} from "../../StarterTrial/Credits/ChangeWorkspacePlanTableCredits";
import { computeProratedPrice, type TierChangeOverride } from "../../../../utils/prorateUtils";
import Button from "../../Button";
import { useStore } from "zustand";
import { useGlobalStore } from "../../../../../state/StoreContext";
import { fetchCheckoutUrlAndRedirect } from "../../../../../backendapi/workspaceApi";
import ComparisonTable from "./ComparisonTableCredits";
import {
    frequencies,
    getPlanPricing,
    getPlanData,
    getCorePlanTiers,
    getDefaultCoreTierIndex,
    getCoreTierForIncludedCredits
} from "../../../../../clientServices/workspaceFeaturesCredits";
import { CreditTierSelector } from "./CreditTierSelector";
import { Chip } from "../../Chip";
import { isFreeOrTrialPlan, isTrialPlan } from "@functions/workspacePlans";
import { Plan } from "@functions/plans/types";
import {
    getPricingTablePlans,
    isFixedSeatPlanWithoutAddon,
    isSeatAddonPurchasePlan
} from "@functions/plans/utilities";
import { CancelPlanController } from "../../Billing/CancelPlan/CancelPlanController";
import { selfServePlanIsExpiring } from "@functions/services/stripe/stripeUtils";
import { usePaidPlanSubscription } from "../../../hooks/usePaidPlanSubscription";
import subscriptionsApi from "../../../../../backendapi/subscriptionsApi";
import { useProjectSelector } from "../../../hooks/useProjectSelector";
import { useTrial } from "../../../hooks/useTrial";
import { trialDurationLabel, getTrialIncludedPlanNames } from "@functions/plans/trialConstants";
import { TrialStartBanner } from "../../Trial/TrialStartBanner";
import { SeatAddonInfo, useSeatAddonInfo } from "../../../hooks/useSeatAddonInfo";
import { SeatAddonInline } from "./SeatAddonInline";
import {
    getEffectiveBaseSeats,
    getWorkspacePlanLimits
} from "../../../../../clientServices/workspaceLimits";
import { buildPaidPlanCheckoutPayload } from "./checkoutPayload";
import { DowngradePlanTargets } from "../../../../../state/types";
import { getIncludedCredits, getPricePerCredit } from "../../../../utils/ubpUtils";
import { getEmailType } from "../../../../emailValidation";
import { computePrepaidCreditPacks } from "@functions/plans/information";

type PlanHighlightRow = { isDelistedPlan?: boolean; isCurrentPlan?: boolean };

export function getFrequencySelectorVisibilityForPlanKey(
    planData: Record<string, PlanHighlightRow | undefined>,
    planKey: string
): "none" | "visible" | "invisible" {
    const core = planData[Plan.UbpCore];
    const coreAligns = Boolean(core && !core.isDelistedPlan && !core.isCurrentPlan);
    if (!coreAligns) {
        return "none";
    }
    return planKey === Plan.UbpCore ? "visible" : "invisible";
}

function shouldAlignCoreTierSelectorAcrossColumns(
    planData: Record<string, PlanHighlightRow | undefined>,
    plansOnTable: readonly Plan[]
): boolean {
    if (!plansOnTable.includes(Plan.UbpCore)) {
        return false;
    }
    const core = planData[Plan.UbpCore];
    return Boolean(core && !core.isDelistedPlan && core.isCurrentPlan);
}

export function renderCoreTierSelectorSlotForColumnAlignment({
    planKey,
    planData,
    plansOnTable,
    hasExtraPlanColumn,
    tiers,
    selectedTier,
    setSelectedTierIndex,
    isAnnual,
    isOnACorePlan,
    currentCoreTierIndex
}: {
    planKey: string;
    planData: Record<string, PlanHighlightRow | undefined>;
    plansOnTable: readonly Plan[];
    hasExtraPlanColumn: boolean;
    tiers: React.ComponentProps<typeof CreditTierSelector>["tiers"];
    selectedTier: React.ComponentProps<typeof CreditTierSelector>["selectedTier"];
    setSelectedTierIndex: (index: number) => void;
    isAnnual: boolean;
    isOnACorePlan: boolean;
    currentCoreTierIndex: number;
}): React.ReactNode {
    // if (!shouldAlignCoreTierSelectorAcrossColumns(planData, plansOnTable)) {
    //     return null;
    // }
    const selector = (
        <CreditTierSelector
            tiers={tiers}
            selectedTier={selectedTier}
            onSelect={(tier) => {
                const idx = tiers.indexOf(tier);
                setSelectedTierIndex(idx >= 0 ? idx : 0);
            }}
            isAnnual={isAnnual}
            baseTier={tiers[0]}
            compact={hasExtraPlanColumn}
            currentSubscribedBundleIndex={isOnACorePlan ? currentCoreTierIndex : null}
        />
    );
    if (planKey === Plan.UbpCore) {
        return selector;
    }
    return (
        <div className="pointer-events-none invisible w-full" aria-hidden>
            {selector}
        </div>
    );
}

export function planDataHasLegacyPlan(
    planData: Record<string, { isDelistedPlan?: boolean }>
): boolean {
    return Object.values(planData).some((row) => row?.isDelistedPlan);
}

type PlanCardAction = "purchase" | "downgrade" | "current" | "contactSales";

type TrialUsage = { plan?: string; trial?: { isTrial?: boolean } };

function buildUsageForTrialChecks(usage: TrialUsage | null | undefined) {
    return {
        plan: usage?.plan || "",
        trial: usage?.trial ? { isTrial: Boolean(usage.trial.isTrial) } : undefined
    };
}

function getPlanCardAction({
    planKey,
    currentPlan,
    usage
}: {
    planKey: string;
    currentPlan: string;
    usage: TrialUsage | null | undefined;
}): PlanCardAction {
    const usageForTrialChecks = buildUsageForTrialChecks(usage);

    if (planKey === Plan.Enterprise) {
        return "contactSales";
    }
    if (currentPlan === planKey) {
        return "current";
    }
    if (planKey === Plan.Public) {
        return "downgrade";
    }

    const isDowngradeToBasic =
        planKey === Plan.UbpBasic &&
        (currentPlan === Plan.UbpGrowth || currentPlan === Plan.LegacyStarter) &&
        !isTrialPlan(usageForTrialChecks);
    const isDowngradeToCore =
        planKey === Plan.UbpCore &&
        currentPlan === Plan.UbpGrowth &&
        !isTrialPlan(usageForTrialChecks);

    if (isDowngradeToBasic || isDowngradeToCore) {
        return "downgrade";
    }

    return "purchase";
}

function shouldShowSwitchToPublicPlanCta({
    planKey,
    usage
}: {
    planKey: string;
    usage: TrialUsage | null | undefined;
}): boolean {
    const usageForTrialChecks = buildUsageForTrialChecks(usage);
    return planKey === Plan.Public && isFreeOrTrialPlan(usageForTrialChecks);
}

function shouldShowSeatAddonPurchaseControl({
    planKey,
    action
}: {
    planKey: string;
    action: PlanCardAction;
}): boolean {
    return action === "purchase" && isSeatAddonPurchasePlan(planKey);
}

function getSeatAddonCheckoutQuantity({
    planKey,
    action,
    seatInfo,
    checked
}: {
    planKey: string;
    action: PlanCardAction;
    seatInfo?: { defaultSeatsToAdd: number; extraSeatsNeeded: number } | null;
    checked: boolean;
}): number {
    if (!shouldShowSeatAddonPurchaseControl({ planKey, action })) {
        return 0;
    }
    if (!seatInfo || !checked) {
        return 0;
    }
    if (seatInfo.extraSeatsNeeded <= 0 || seatInfo.defaultSeatsToAdd <= 0) {
        return 0;
    }
    return seatInfo.defaultSeatsToAdd;
}

const PlanColumn = ({ children, className = "", recommended, recommendedText }) => {
    return (
        <div
            className={classnames(
                "relative flex h-full w-full flex-col gap-6 overflow-visible p-6 pt-7 md:h-auto 2xl:p-8",
                className,
                recommended && "recommendedPlan -my-2 md:-mx-2 md:-my-1"
            )}
        >
            {recommended && recommendedText && (
                <div className="recommendedTag">
                    <span className="recommendedText">{recommendedText}</span>
                </div>
            )}
            {children}
        </div>
    );
};

PlanColumn.PlanIntro = ({
    planIcon,
    planName,
    planDescription,
    planKey,
    frequency,
    setFrequency,
    recommended,
    className = "",
    isDelistedPlan = false,
    isCurrentPlan = false,
    priceOverride = null,
    stackFrequencySelector = false,
    tierChangeOverride = null,
    frequencySelectorVisibility
}) => {
    const legacyFrequencySelectorVisible =
        (planKey === Plan.UbpBasic || planKey === Plan.UbpCore || planKey === Plan.UbpGrowth) &&
        !isDelistedPlan &&
        !isCurrentPlan;
    const frequencySelectorState: "none" | "visible" | "invisible" =
        frequencySelectorVisibility === undefined
            ? legacyFrequencySelectorVisible
                ? "visible"
                : "invisible"
            : frequencySelectorVisibility;

    return (
        <div className={classnames("flex flex-col gap-6", className)}>
            <div className="flex flex-row items-center justify-between gap-2">
                <div className="flex flex-row items-center gap-2 text-2xl font-semibold leading-9 text-gray-900">
                    <i
                        className={classnames(
                            planIcon,
                            recommended ? "text-yellow-500" : "text-brand"
                        )}
                    ></i>
                    <span className="whitespace-nowrap font-semibold">{planName}</span>
                </div>
                <Chip color="gray">{planDescription}</Chip>
            </div>
            <div
                className={classnames(
                    "flex flex-col gap-2",
                    stackFrequencySelector ? "flex-col items-start" : "flex-row justify-between"
                )}
            >
                {isDelistedPlan ? (
                    <div className="flex flex-col">
                        <span className="flex text-2xl font-medium text-tertiary">Legacy Plan</span>
                        <span className="text-sm text-tertiary">
                            No longer available for purchase
                        </span>
                    </div>
                ) : (
                    <PlanPrice
                        planType={planKey}
                        frequency={frequency}
                        priceOverride={priceOverride}
                        tierChangeOverride={tierChangeOverride}
                    />
                )}
                {frequencySelectorState !== "none" && (
                    <div
                        className={classnames(
                            frequencySelectorState === "visible"
                                ? "visible w-full"
                                : "invisible w-full"
                        )}
                    >
                        <PlanFrequencySelector
                            frequencyOptions={frequencies}
                            frequency={frequency}
                            setFrequency={setFrequency}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

PlanColumn.SpecialFeature = ({ icon, children }) => {
    return (
        <div className="flex flex-row items-center gap-2 self-stretch border-b-1 border-t-1 border-gray-200 py-6 text-sm text-primary">
            <i className={icon}></i>
            <div>{children}</div>
        </div>
    );
};

PlanColumn.FeatureList = ({ listTitle, listSubtitle, titleClassName = "", children }) => {
    return (
        <div className="flex flex-col gap-2">
            <span className={classnames("text-sm font-semibold text-primary", titleClassName)}>
                {listTitle}
            </span>
            {listSubtitle && <span className="text-sm text-primary">{listSubtitle}</span>}
            <div className="flex flex-col gap-2 pl-1">{children}</div>
        </div>
    );
};

const getCtaText = ({
    planKey,
    planName,
    frequency,
    currentPlan,
    usage,
    isExpiring,
    isUpgradingTier = false,
    isDowngradingTier = false
}) => {
    const ctaFrequency =
        planKey === Plan.UbpBasic || planKey === Plan.UbpCore
            ? frequency.labelWithoutDiscount + " "
            : null;
    const action = getPlanCardAction({ planKey, currentPlan, usage });

    if (action === "contactSales") {
        return "Contact Sales";
    }
    if (action === "current") {
        if (isExpiring) {
            return "Reactivate Plan";
        }
        if (isUpgradingTier) {
            return "Upgrade Bundle";
        }
        if (isDowngradingTier) {
            return "Downgrade Bundle";
        }
        return "Current Plan";
    }
    if (action === "downgrade") {
        if (shouldShowSwitchToPublicPlanCta({ planKey, usage })) {
            return "Switch to Public Plan";
        }
        return (
            <>
                <span className="hidden lg:inline">
                    Downgrade to {ctaFrequency}
                    {planName}
                </span>
                <span className="lg:hidden">Downgrade to {planName}</span>
            </>
        );
    }

    return (
        <>
            <span className="inline">Upgrade to {planName}</span>
        </>
    );
};

export const PlanButton = ({
    planDetails,
    planKey,
    usage,
    handlePlanDowngrade,
    handleSalesFormClick,
    currentPlan,
    frequency,
    isProcessing,
    handlePlanPurchase,
    isEligibleForTrial = false,
    isUpgradingTier = false,
    isDowngradingTier = false,
    onBundleChange
}) => {
    const [loading, setLoading] = useState(false);
    const projectSelector = useProjectSelector();
    const planPricing = useMemo(() => getPlanPricing(projectSelector), [projectSelector]);

    const { subscription, fetchPaidPlanSubscription } = usePaidPlanSubscription();
    const isExpiring = selfServePlanIsExpiring({ plan: currentPlan, subscription });
    const planAction = getPlanCardAction({ planKey, currentPlan, usage });

    const handlePlanSelect = async () => {
        if (planAction === "current" && isExpiring) {
            try {
                setLoading(true);
                await subscriptionsApi.reactivatePaidPlanSubscription(usage?.id);
                await fetchPaidPlanSubscription({ refetch: true });
            } catch (error) {
                setLoading(false);
                swal.fire({ title: "Error", text: error.message, icon: "error", timer: 5000 });
            }
        } else if (planAction === "contactSales") {
            handleSalesFormClick();
        } else if (planAction === "downgrade" || isDowngradingTier) {
            if (isDowngradingTier) {
                onBundleChange?.("downgrade");
            }
            try {
                handlePlanDowngrade(planKey);
            } catch (error) {
                setLoading(false);
                swal.fire({ title: "Error", text: error.message, icon: "error", timer: 5000 });
            }
        } else {
            if (isUpgradingTier) {
                onBundleChange?.("upgrade");
            }
            handlePlanPurchase(planPricing[planKey][frequency.interval]);
        }

        setLoading(false);
    };
    const shouldDemoteCTA =
        isEligibleForTrial && planDetails.ctaType === "upgrade" && planKey !== Plan.Enterprise;
    const buttonType = shouldDemoteCTA ? "secondary" : planDetails.ctaType;

    return (
        <Button
            type={buttonType}
            onClick={handlePlanSelect}
            loading={isProcessing === planKey || loading}
            disabled={
                currentPlan === planKey && !isExpiring && !isUpgradingTier && !isDowngradingTier
            }
            className="self-stretch"
        >
            {getCtaText({
                planKey,
                planName: planDetails.name,
                frequency,
                currentPlan,
                usage,
                isExpiring,
                isUpgradingTier,
                isDowngradingTier
            })}
        </Button>
    );
};

export const renderPlanColumn = ({
    planKey,
    planDetails,
    frequency,
    setFrequency,
    recommended = false,
    fsrc = "",
    alwaysShowPublic = false,
    getPlanButton,
    seatAddonSlot = null,
    tierSelectorSlot = null,
    priceOverride = null,
    hasLegacyPlan = false,
    tierChangeOverride = null,
    frequencySelectorVisibility
}: {
    planKey: string;
    planDetails: any;
    frequency: any;
    setFrequency: (f: any) => void;
    recommended?: boolean;
    fsrc?: string;
    alwaysShowPublic?: boolean;
    getPlanButton: (args: { planKey: string; planDetails: any }) => React.ReactNode;
    seatAddonSlot?: React.ReactNode;
    tierSelectorSlot?: React.ReactNode;
    priceOverride?: { price: number; monthlyPrice?: number } | null;
    hasLegacyPlan?: boolean;
    tierChangeOverride?: TierChangeOverride | null;
    frequencySelectorVisibility?: "none" | "visible" | "invisible";
}) => {
    const {
        icon,
        name,
        description,
        highlightedFeatures,
        planLimits,
        hasCustomLimits,
        isDelistedPlan,
        isCurrentPlan
    } = planDetails;

    return (
        <PlanColumn
            key={planKey}
            className={classnames(
                planKey === Plan.Public && !alwaysShowPublic ? "hidden xl:flex" : "",
                recommended && "!m-0"
            )}
            recommended={!isDelistedPlan && (planDetails.recommended || recommended)}
            recommendedText={
                isDelistedPlan
                    ? undefined
                    : hasCustomLimits
                      ? "YOUR CURRENT LIMITS"
                      : planDetails.recommendedText
            }
        >
            <div className="flex flex-col">
                <PlanColumn.PlanIntro
                    planIcon={icon}
                    planName={name}
                    planDescription={description}
                    planKey={planKey}
                    frequency={frequency}
                    setFrequency={setFrequency}
                    recommended={planDetails.recommended || recommended}
                    className={classnames((planDetails.recommended || recommended) && "pt-0.5")}
                    isDelistedPlan={isDelistedPlan}
                    isCurrentPlan={isCurrentPlan}
                    priceOverride={priceOverride}
                    stackFrequencySelector={hasLegacyPlan}
                    tierChangeOverride={tierChangeOverride}
                    {...(frequencySelectorVisibility !== undefined
                        ? { frequencySelectorVisibility }
                        : {})}
                />
                <div className="flex flex-col gap-2 pt-4">
                    {tierSelectorSlot}
                    {seatAddonSlot}
                    {getPlanButton({ planKey, planDetails })}
                </div>
            </div>
            <PlanPrivacy planKey={planKey} planDetails={planDetails} />
            {/* Plan Limits Section */}
            {planLimits && planLimits.length > 0 && (
                <div className="flex flex-col gap-2 border-b-1 border-gray-200 pb-4">
                    {isCurrentPlan && (
                        <span className="text-sm font-semibold text-primary">
                            Your current workspace includes:
                        </span>
                    )}
                    {planLimits.map((limit, index) => {
                        const formattedValue =
                            typeof limit.value === "number"
                                ? limit.value.toLocaleString()
                                : limit.value;
                        const showTierChangeStrikethrough =
                            tierChangeOverride && limit.key === "credits";

                        return (
                            <FeatureListing
                                key={index}
                                boldText={formattedValue}
                                strikethroughOverrideText={
                                    showTierChangeStrikethrough &&
                                    tierChangeOverride?.currentCredits != null
                                        ? tierChangeOverride.currentCredits.toLocaleString()
                                        : undefined
                                }
                                text={limit.text}
                            />
                        );
                    })}
                </div>
            )}
            {/* Highlighted Features Section */}
            <div className="flex flex-col gap-2">
                {highlightedFeatures.map((feature, index) => (
                    <FeatureListing
                        key={index}
                        {...feature}
                        highlight={feature.highlight?.includes(fsrc)}
                    />
                ))}
            </div>
        </PlanColumn>
    );
};

export function PlansComparisonCredits({
    fsrc = "",
    excludePublic = false,
    closeModal,
    successUrl
}) {
    const [fetchingCheckoutUrl, setFetchingCheckoutUrl] = useState(null);
    const projectSelector = useProjectSelector();
    const globalStore = useGlobalStore();
    const usage = useStore(globalStore, (state) => state.getUsage());
    const workspace_id = useStore(globalStore, (state) => state.getWorkspace()?.id);
    const uid = useStore(globalStore, (state) => state.user.uid);
    const { isEligibleForTrial, activateTrial, isActivating: isActivatingTrial } = useTrial();
    const handleActivateTrial = () => activateTrial({ funnelSource: fsrc, onSuccess: closeModal });

    useEffect(() => {
        window?.eventTracker?.event({
            type: "Workspace Plans Seen",
            data: {
                funnelSource: fsrc,
                workspaceId: workspace_id,
                uid: uid,
                currentPlan: usage?.plan
            }
        });
    }, []);

    function handlePlanSelected(planData) {
        const eventData: Record<string, any> = {
            funnelSource: fsrc,
            clickedFrom: "workspace_plans",
            workspaceId: workspace_id,
            uid: uid,
            currentPlan: usage?.plan,
            planType: planData.planType,
            seatAddonQuantity: planData.seatAddonQuantity || 0
        };
        if (planData.coreTierCredits) {
            eventData.coreTierCredits = planData.coreTierCredits;
        }
        window?.eventTracker?.event({ type: "Clicked Paid Plan", data: eventData });
        handlePlanPurchase({
            priceId: planData.priceId,
            price: planData.totalPrice,
            interval: planData.interval,
            planType: planData.planType,
            seatAddonQuantity: planData.seatAddonQuantity || 0,
            tierCredits: planData.tierCredits
        });
    }

    async function handlePlanPurchase({
        priceId,
        price,
        interval,
        planType,
        seatAddonQuantity = 0,
        tierCredits
    }: {
        priceId: string;
        price: string;
        interval: string;
        planType: string;
        seatAddonQuantity?: number;
        tierCredits?: number;
    }) {
        const data = buildPaidPlanCheckoutPayload({
            workspaceId: workspace_id,
            uid,
            planType,
            funnelSource: fsrc,
            priceId,
            price,
            interval,
            seatAddonQuantity,
            successUrl,
            tierCredits
        });
        if (fetchingCheckoutUrl) {
            return;
        }
        setFetchingCheckoutUrl(planType);
        await fetchCheckoutUrlAndRedirect(data);
        setFetchingCheckoutUrl(null);
    }

    return (
        <PlansTable
            fsrc={fsrc}
            currentPlan={usage?.plan}
            usage={usage}
            isProcessing={fetchingCheckoutUrl || (isActivatingTrial ? Plan.UbpPremiumTrial : null)}
            handlePlanPurchase={handlePlanSelected}
            excludePublic={excludePublic}
            closeModal={closeModal}
            isEligibleForTrial={isEligibleForTrial}
            onActivateTrial={handleActivateTrial}
            isActivatingTrial={isActivatingTrial}
        />
    );
}

export function PlanHighlights({
    planData,
    planDataExcludingPublic,
    excludePublic,
    columnFrequencyForPlanKey,
    setFrequency,
    fsrc,
    getSeatAddonSlot,
    getCoreTierSelectorSlot,
    getCorePriceOverride,
    hasLegacyPlan,
    usage,
    setSelectedDowngradePlan,
    handleSalesFormClick,
    currentPlan,
    isProcessing,
    wrappedHandlePlanPurchase,
    isEligibleForTrial
}: {
    planData: Record<string, any>;
    planDataExcludingPublic: Record<string, any>;
    excludePublic: boolean;
    columnFrequencyForPlanKey: (planKey: string) => any;
    setFrequency: (f: any) => void;
    fsrc: string;
    getSeatAddonSlot: (planKey: string) => React.ReactNode;
    getCoreTierSelectorSlot: (planKey: string) => React.ReactNode;
    getCorePriceOverride: (planKey: string) => { price: number; monthlyPrice?: number } | null;
    hasLegacyPlan: boolean;
    usage: any;
    setSelectedDowngradePlan: React.Dispatch<React.SetStateAction<DowngradePlanTargets | null>>;
    handleSalesFormClick: () => void;
    currentPlan: string;
    isProcessing: string | null;
    wrappedHandlePlanPurchase: (planData: any) => void;
    isEligibleForTrial: boolean;
}) {
    return (
        <div className="planHighlights">
            <div className="relative mt-2 hidden flex-row gap-1 divide-x divide-y-0 divide-brand overflow-visible rounded-md border border-brand bg-white transition duration-200 lg:mt-7 lg:flex">
                {Object.entries(planData)
                    .filter(([key]) => !excludePublic || key !== Plan.Public)
                    .map(([key, details]) => {
                        return renderPlanColumn({
                            planKey: key,
                            planDetails: details,
                            frequency: columnFrequencyForPlanKey(key),
                            setFrequency,
                            fsrc,
                            seatAddonSlot: getSeatAddonSlot(key),
                            tierSelectorSlot: getCoreTierSelectorSlot(key),
                            priceOverride: getCorePriceOverride(key),
                            hasLegacyPlan,
                            frequencySelectorVisibility: getFrequencySelectorVisibilityForPlanKey(
                                planData,
                                key
                            ),
                            getPlanButton: ({ planKey, planDetails }) => (
                                <PlanButton
                                    planKey={planKey}
                                    planDetails={planDetails}
                                    usage={usage}
                                    handlePlanDowngrade={() =>
                                        setSelectedDowngradePlan(planKey as DowngradePlanTargets)
                                    }
                                    handleSalesFormClick={handleSalesFormClick}
                                    currentPlan={currentPlan}
                                    frequency={columnFrequencyForPlanKey(planKey)}
                                    isProcessing={isProcessing}
                                    handlePlanPurchase={wrappedHandlePlanPurchase}
                                    isEligibleForTrial={isEligibleForTrial}
                                />
                            )
                        });
                    })}
            </div>

            <div className="relative flex w-full flex-1 flex-col items-start divide-y divide-brand overflow-visible rounded-lg border border-brand bg-white md:mt-2 md:flex-row md:items-stretch md:divide-x md:divide-y-0 lg:hidden">
                {Object.entries(planDataExcludingPublic)
                    .filter(([key]) => key !== Plan.Public)
                    .map(([key, details]) =>
                        renderPlanColumn({
                            planKey: key,
                            planDetails: details,
                            frequency: columnFrequencyForPlanKey(key),
                            setFrequency,
                            fsrc,
                            seatAddonSlot: getSeatAddonSlot(key),
                            tierSelectorSlot: getCoreTierSelectorSlot(key),
                            priceOverride: getCorePriceOverride(key),
                            hasLegacyPlan,
                            frequencySelectorVisibility: getFrequencySelectorVisibilityForPlanKey(
                                planData,
                                key
                            ),
                            getPlanButton: ({ planKey, planDetails }) => (
                                <PlanButton
                                    planKey={planKey}
                                    planDetails={planDetails}
                                    usage={usage}
                                    handlePlanDowngrade={() =>
                                        setSelectedDowngradePlan(planKey as DowngradePlanTargets)
                                    }
                                    handleSalesFormClick={handleSalesFormClick}
                                    currentPlan={currentPlan}
                                    frequency={columnFrequencyForPlanKey(planKey)}
                                    isProcessing={isProcessing}
                                    handlePlanPurchase={wrappedHandlePlanPurchase}
                                    isEligibleForTrial={isEligibleForTrial}
                                />
                            )
                        })
                    )}
            </div>
        </div>
    );
}

export function PlansTable({
    fsrc = "",
    currentPlan = "",
    usage,
    isProcessing,
    handlePlanPurchase,
    excludePublic,
    closeModal,
    isEligibleForTrial,
    onActivateTrial,
    isActivatingTrial
}: {
    fsrc: string;
    currentPlan: string;
    usage: any;
    isProcessing: string | null;
    handlePlanPurchase: (frequency: any) => void;
    excludePublic: boolean;
    closeModal: () => void;
    isEligibleForTrial: boolean;
    onActivateTrial: () => Promise<void>;
    isActivatingTrial: boolean;
}) {
    const [frequency, setFrequency] = useState(frequencies[1]);
    const [selectedDowngradePlan, setSelectedDowngradePlan] = useState<DowngradePlanTargets | null>(
        null
    );
    const projectSelector = useProjectSelector();
    const { subscription } = usePaidPlanSubscription();
    const globalStore = useGlobalStore();
    const planHistoricalUsageData = useStore(
        globalStore,
        (state) => state.workspacePlanHistoricalUsageDataById[state.currentWorkspaceId]
    );

    const corePlanTiers = useMemo(() => getCorePlanTiers(projectSelector), [projectSelector]);
    // usage.ubp.includedCredits is per-cycle (×12 for annual), but corePlanTiers uses monthly credits.
    // Normalize to monthly before comparing using usage.activePlanSubscription.isMonthly.
    // When isMonthly is true, credits are already monthly. When false (annual), divide by 12.
    // When undefined (no subscription), assumes annual (divides by 12) as a safe default.
    // Takes usage explicitly to avoid stale closure bugs in useState/useMemo.
    const getMonthlyCredits = (credits: number, usageRef: any) => {
        const { isMonthly } = usageRef?.activePlanSubscription || {};
        return isMonthly ? credits : credits / 12;
    };
    const [selectedCoreTierIndex, setSelectedCoreTierIndex] = useState(() => {
        if (currentPlan === Plan.UbpCore && usage?.ubp?.includedCredits != null) {
            const monthly = getMonthlyCredits(usage.ubp.includedCredits, usage);
            const index = corePlanTiers.findIndex((t) => t.credits === monthly);
            if (index >= 0) return index;
        }
        const userEmail = typeof window !== "undefined" ? window.user?.email : undefined;
        const isNonBusinessEmail =
            typeof userEmail === "string" && getEmailType(userEmail) !== "Business";
        return getDefaultCoreTierIndex(corePlanTiers, { isPersonalEmail: isNonBusinessEmail });
    });
    const selectedCoreTier = corePlanTiers[selectedCoreTierIndex] || corePlanTiers[0];

    const coreSeatInfo = useSeatAddonInfo(Plan.UbpCore);
    const seatAddonInfoByPlan: Record<string, SeatAddonInfo> = useMemo(
        () => ({ [Plan.UbpCore]: coreSeatInfo }),
        [coreSeatInfo]
    );
    const occupancySeatInfo = useMemo(
        () => Object.values(seatAddonInfoByPlan)[0] || null,
        [seatAddonInfoByPlan]
    );
    const [seatAddonChecked, setSeatAddonChecked] = useState<Record<string, boolean>>({});

    useEffect(() => {
        setSeatAddonChecked((previousChecked) => {
            const nextChecked = { ...previousChecked };
            let hasChanges = false;
            Object.entries(seatAddonInfoByPlan).forEach(([planKey, seatInfo]) => {
                if (previousChecked[planKey] !== undefined) {
                    return;
                }
                if (!seatInfo.isLoading && seatInfo.extraSeatsNeeded > 0) {
                    nextChecked[planKey] = true;
                    hasChanges = true;
                }
            });
            return hasChanges ? nextChecked : previousChecked;
        });
    }, [seatAddonInfoByPlan]);

    const getSeatAddonInfo = (planKey: string) => seatAddonInfoByPlan[planKey];

    const getEffectiveSeatAddonChecked = (planKey: string, seatInfo: SeatAddonInfo) =>
        seatAddonChecked[planKey] ?? seatInfo.extraSeatsNeeded > 0;

    const getSeatAddonSlot = (planKey: string) => {
        const action = getPlanCardAction({ planKey, currentPlan, usage });
        if (shouldShowSeatAddonPurchaseControl({ planKey, action })) {
            const seatInfo = getSeatAddonInfo(planKey);
            if (!seatInfo) {
                return null;
            }
            return (
                <SeatAddonInline
                    seatInfo={seatInfo}
                    checked={getEffectiveSeatAddonChecked(planKey, seatInfo)}
                    onToggle={(checked) =>
                        setSeatAddonChecked((prev) => ({ ...prev, [planKey]: checked }))
                    }
                    isAnnual={frequency.interval === "annually"}
                />
            );
        }
        if (
            isFixedSeatPlanWithoutAddon(planKey) &&
            occupancySeatInfo &&
            !occupancySeatInfo.isLoading
        ) {
            const baseSeats = getEffectiveBaseSeats(planKey, usage);
            if (occupancySeatInfo.totalOccupants > baseSeats) {
                return (
                    <SeatAddonInline
                        seatInfo={{
                            ...occupancySeatInfo,
                            hasSeatAddon: false,
                            baseSeats,
                            extraSeatsNeeded: occupancySeatInfo.totalOccupants - baseSeats,
                            defaultSeatsToAdd: 0,
                            exceedsMax: false
                        }}
                        checked={false}
                        onToggle={() => {}}
                        isAnnual={frequency.interval === "annually"}
                    />
                );
            }
        }
        return null;
    };

    const isAnnual = frequency.interval === "annually";
    const isOnACorePlan = currentPlan === Plan.UbpCore;

    // If the user is on a Core plan, find out which tier they are currently on
    // based on their included credits. This will be used to determine if they are
    // upgrading or downgrading their tier when they select a different one.
    const currentCoreTierIndex = useMemo(() => {
        if (!isOnACorePlan) return -1;
        const currentCredits = usage?.ubp?.includedCredits;
        if (currentCredits == null) return 0;
        const monthly = getMonthlyCredits(currentCredits, usage);
        const index = corePlanTiers.findIndex((t) => t.credits === monthly);
        return index >= 0 ? index : 0;
    }, [
        isOnACorePlan,
        usage?.ubp?.includedCredits,
        corePlanTiers,
        usage?.activePlanSubscription?.isMonthly
    ]);

    const [hasUserChangedTier, setHasUserChangedTier] = useState(false);

    // Always default to showing the user the tier that they're currently subscribed to
    useEffect(() => {
        if (isOnACorePlan && currentCoreTierIndex >= 0 && !hasUserChangedTier) {
            setSelectedCoreTierIndex(currentCoreTierIndex);
        }
    }, [isOnACorePlan, currentCoreTierIndex, hasUserChangedTier]);

    const isCoreTierBeingUpgraded = isOnACorePlan && selectedCoreTierIndex > currentCoreTierIndex;
    const isCoreTierBeingDowngraded = isOnACorePlan && selectedCoreTierIndex < currentCoreTierIndex;
    const hasCoreTierChanged = isCoreTierBeingUpgraded || isCoreTierBeingDowngraded;

    // Derive the actual billing interval for the current plan from usage.activePlanSubscription.
    // Must be above coreTierChangeOverride so it can be used there.
    // Falls back to the UI frequency toggle if activePlanSubscription is not available.
    const currentPlanFrequency = useMemo(() => {
        const { isMonthly } = usage?.activePlanSubscription || {};
        if (isMonthly === true) {
            return frequencies.find((f) => f.interval === "monthly") || frequencies[0];
        }
        if (isMonthly === false) {
            return frequencies.find((f) => f.interval === "annually") || frequencies[1];
        }
        return frequency;
    }, [usage?.activePlanSubscription, frequency]);

    // Sync the frequency toggle to the actual subscription interval when available,
    // so isAnnual and all derived values reflect the real billing cycle.
    useEffect(() => {
        if (currentPlanFrequency && currentPlanFrequency.interval !== frequency.interval) {
            setFrequency(currentPlanFrequency);
        }
    }, [currentPlanFrequency]);

    const coreTierChangeOverride = useMemo(() => {
        // Only compute prorate override for upgrades. Downgrades show the normal tier price.
        // Wait for subscription to load so isAnnual (via currentPlanFrequency) is accurate.
        if (!isCoreTierBeingUpgraded || !subscription) return null;

        const periodEnd = usage?.activePlanSubscription?.currentPeriodEnd;
        const periodStart = usage?.activePlanSubscription?.currentPeriodStart;
        const renewalDate = periodEnd
            ? new Date(periodEnd.seconds ? periodEnd.seconds * 1000 : periodEnd).toLocaleDateString(
                  "en-US",
                  { day: "numeric", month: "short", year: "numeric" }
              )
            : "next billing cycle";

        const currentTier = corePlanTiers[currentCoreTierIndex];
        const currentCredits = currentTier
            ? isAnnual
                ? currentTier.credits * 12
                : currentTier.credits
            : null;

        // All values are per billing cycle (monthly for monthly plans, yearly for annual)
        const newTierPrice = isAnnual
            ? selectedCoreTier.annualPrice * 12
            : selectedCoreTier.monthlyPrice;
        // Use the actual Stripe subscription price when available (matches what the user pays),
        // falling back to the static tier table.
        const currentTierPrice = subscription?.planUnitAmount
            ? subscription.planUnitAmount / 100
            : currentTier
              ? isAnnual
                  ? currentTier.annualPrice * 12
                  : currentTier.monthlyPrice
              : 0;
        const basePricePerCredit = getPricePerCredit({ usage });
        const dynamicPacks = computePrepaidCreditPacks(basePricePerCredit, "core");
        const creditPack = currentTier
            ? dynamicPacks.find((p) => p.credits === currentTier.credits)
            : null;
        const pricePerCredit = creditPack?.pricePerCredit ?? basePricePerCredit;
        // getIncludedCredits returns the per-cycle value (already multiplied by 12 for annual)
        const includedCredits = getIncludedCredits({ usage });
        const cycleCreditsUsed = planHistoricalUsageData?.cycleCredits ?? 0;
        const unusedCredits = Math.max(0, includedCredits - cycleCreditsUsed);
        // Prefer usage doc timestamps, fall back to subscription data (fetched from Stripe directly)
        // which is always fresh even if the usage doc hasn't synced yet after a recent upgrade.
        const cycleStartSeconds = periodStart?.seconds ?? subscription?.periodStart ?? 0;
        const cycleEndSeconds = periodEnd?.seconds ?? subscription?.renewedAt ?? 0;

        // Estimate add-on cost from the workspace's current collaborator limit vs the plan default.
        // usage.limit.collaborators = total seat limit (base + purchased add-ons).
        // getWorkspacePlanLimits gives the plan's default base seats (without add-ons).
        const currentCollabLimit = usage?.limit?.collaborators ?? 0;
        const planDefaultSeats = getWorkspacePlanLimits(Plan.UbpCore, true)?.collaborators ?? 0;
        const purchasedSeatAddons = Math.max(0, currentCollabLimit - planDefaultSeats);
        const seatMonthlyPrice = coreSeatInfo?.pricePerSeat ?? 0;
        const addOnCost = purchasedSeatAddons * seatMonthlyPrice * (isAnnual ? 12 : 1);

        return {
            renewalDate,
            currentCredits,
            newTierPrice,
            currentTierPrice,
            pricePerCredit,
            unusedCredits,
            cycleCreditsUsed,
            cycleStartSeconds,
            cycleEndSeconds,
            isAnnual: isAnnual,
            addOnCost
        };
    }, [
        isCoreTierBeingUpgraded,
        usage,
        corePlanTiers,
        currentCoreTierIndex,
        isAnnual,
        selectedCoreTier,
        planHistoricalUsageData?.cycleCredits,
        coreSeatInfo,
        subscription
    ]);

    const subscribedCoreTier = useMemo(() => {
        if (!isOnACorePlan || typeof usage?.ubp?.includedCredits !== "number") {
            return null;
        }
        return getCoreTierForIncludedCredits(corePlanTiers, usage.ubp.includedCredits);
    }, [isOnACorePlan, usage?.ubp?.includedCredits, corePlanTiers]);

    const pricingTablePlans = getPricingTablePlans(currentPlan);
    const hasExtraPlanColumn = pricingTablePlans.length > 3;

    const getCorePriceOverride = (planKey: string) => {
        if (planKey !== Plan.UbpCore) {
            return null;
        }
        if (isOnACorePlan) {
            // When downgrading, show the selected (lower) tier's price.
            // When on the current tier (no change), show the subscribed tier's price.
            // When upgrading, coreTierChangeOverride handles the display (this returns subscribed price as the base).
            if (isCoreTierBeingDowngraded) {
                return {
                    price: isAnnual ? selectedCoreTier.annualPrice : selectedCoreTier.monthlyPrice,
                    monthlyPrice: selectedCoreTier.monthlyPrice
                };
            }
            if (!subscribedCoreTier) {
                return null;
            }
            const priceAnnual = currentPlanFrequency.interval === "annually";
            return {
                price: priceAnnual
                    ? subscribedCoreTier.annualPrice
                    : subscribedCoreTier.monthlyPrice,
                monthlyPrice: subscribedCoreTier.monthlyPrice
            };
        }
        return {
            price: isAnnual ? selectedCoreTier.annualPrice : selectedCoreTier.monthlyPrice,
            monthlyPrice: selectedCoreTier.monthlyPrice
        };
    };

    const columnFrequencyForPlanKey = (planKey: string) =>
        planKey === currentPlan && currentPlan === Plan.UbpCore ? currentPlanFrequency : frequency;

    const wrappedHandlePlanPurchase = (planData: any) => {
        const planKey = planData.planType;
        const action = getPlanCardAction({ planKey, currentPlan, usage });
        const seatInfo = getSeatAddonInfo(planKey);
        const quantity = getSeatAddonCheckoutQuantity({
            planKey,
            action,
            seatInfo,
            checked: seatInfo ? getEffectiveSeatAddonChecked(planKey, seatInfo) : false
        });

        if (planKey === Plan.UbpCore && (!isOnACorePlan || hasCoreTierChanged)) {
            const tierPrice = isAnnual
                ? selectedCoreTier.annualPrice
                : selectedCoreTier.monthlyPrice;

            handlePlanPurchase({
                ...planData,
                totalPrice: `$${isAnnual ? tierPrice * 12 : tierPrice}`,
                seatAddonQuantity: quantity,
                coreTierCredits: selectedCoreTier.credits,
                tierCredits: selectedCoreTier.credits
            });
            return;
        }

        handlePlanPurchase({ ...planData, seatAddonQuantity: quantity });
    };

    const handleSalesFormClick = () => {
        window?.open("https://roboflow.com/sales", "_blank");
        window?.eventTracker?.event({
            type: "Clicked Sales form",
            data: {
                funnelSource: fsrc,
                clickedFrom: "workspace_plans",
                workspaceId: usage?.id,
                uid: window?.user?.uid,
                currentPlan: usage?.plan
            }
        });
    };

    const handleBundleChange = (direction: "upgrade" | "downgrade") => {
        const proratedPrice = coreTierChangeOverride
            ? computeProratedPrice(coreTierChangeOverride)
            : null;
        window?.eventTracker?.event({
            type: direction === "upgrade" ? "Clicked Upgrade Bundle" : "Clicked Downgrade Bundle",
            data: {
                workspaceId: usage?.id,
                currentPlan: usage?.plan,
                initialPrice: coreTierChangeOverride?.newTierPrice ?? null,
                proratedPrice
            }
        });
    };

    const coreTierOption = {};

    if (hasCoreTierChanged) {
        // User changed tier — show the new selection's pricing
        coreTierOption.selectedCoreTier = selectedCoreTier;
    } else if (isOnACorePlan && subscribedCoreTier) {
        // Current Core user, no change — show their subscribed tier with actual interval
        coreTierOption.selectedCoreTier = subscribedCoreTier;
        coreTierOption.coreTierInterval = currentPlanFrequency.interval;
    } else if (!isOnACorePlan) {
        // Non-Core user browsing — show the selected tier's pricing
        coreTierOption.selectedCoreTier = selectedCoreTier;
    }

    const plansOnTable = pricingTablePlans;
    const planData = getPlanData(
        currentPlan,
        frequency,
        plansOnTable as any,
        usage,
        projectSelector,
        coreTierOption
    );
    const planDataExcludingPublic = getPlanData(
        currentPlan,
        frequency,
        getPricingTablePlans(currentPlan, [Plan.Public]) as any,
        usage,
        projectSelector,
        coreTierOption
    );

    const currentPlanMergeOptions =
        isOnACorePlan && subscribedCoreTier
            ? {
                  selectedCoreTier: subscribedCoreTier,
                  coreTierInterval: currentPlanFrequency.interval
              }
            : {};

    if (currentPlan && planData[currentPlan]) {
        const currentPlanDataWithActualInterval = getPlanData(
            currentPlan,
            currentPlanFrequency,
            [currentPlan] as any,
            usage,
            projectSelector,
            currentPlanMergeOptions
        )[currentPlan];

        if (currentPlanDataWithActualInterval) {
            planData[currentPlan] = {
                ...planData[currentPlan],
                ...currentPlanDataWithActualInterval,
                isCurrentPlan: true
            };
        }
    }

    if (currentPlan && planDataExcludingPublic[currentPlan]) {
        const currentPlanDataWithActualInterval = getPlanData(
            currentPlan,
            currentPlanFrequency,
            [currentPlan] as any,
            usage,
            projectSelector,
            currentPlanMergeOptions
        )[currentPlan];

        if (currentPlanDataWithActualInterval) {
            planDataExcludingPublic[currentPlan] = {
                ...planDataExcludingPublic[currentPlan],
                ...currentPlanDataWithActualInterval,
                isCurrentPlan: true
            };
        }
    }

    // Override Core plan credits limit based on selected tier
    if (selectedCoreTier) {
        const tierCredits = isAnnual ? selectedCoreTier.credits * 12 : selectedCoreTier.credits;
        const creditsText = `credits per ${isAnnual ? "year, loaded upfront" : "month"}`;
        for (const data of [planData, planDataExcludingPublic]) {
            if (data[Plan.UbpCore]?.planLimits) {
                data[Plan.UbpCore].planLimits = data[Plan.UbpCore].planLimits.map((l) =>
                    l.key === "credits" ? { ...l, value: tierCredits, text: creditsText } : l
                );
            }
        }
    }

    const getCoreTierSelectorSlot = (planKey: string) =>
        renderCoreTierSelectorSlotForColumnAlignment({
            planKey,
            planData,
            plansOnTable,
            hasExtraPlanColumn,
            tiers: corePlanTiers,
            selectedTier: selectedCoreTier,
            setSelectedTierIndex: setSelectedCoreTierIndex,
            isAnnual,
            isOnACorePlan,
            currentCoreTierIndex
        });

    const hasLegacyPlan = planDataHasLegacyPlan(planData);

    const PlanHighlights = () => {
        return (
            <div className="planHighlights">
                {/* Large screens */}
                <div className="relative mt-2 hidden flex-row gap-1 divide-x divide-y-0 divide-brand overflow-visible rounded-md border border-brand bg-white transition duration-200 lg:mt-7 lg:flex">
                    {Object.entries(planData)
                        .filter(([key]) => !excludePublic || key !== Plan.Public)
                        .map(([key, details]) => {
                            return renderPlanColumn({
                                planKey: key,
                                planDetails: details,
                                frequency,
                                setFrequency,
                                fsrc,
                                seatAddonSlot: getSeatAddonSlot(key),
                                tierSelectorSlot: getCoreTierSelectorSlot(key),
                                priceOverride: getCorePriceOverride(key),
                                hasLegacyPlan,
                                tierChangeOverride:
                                    key === Plan.UbpCore ? coreTierChangeOverride : null,
                                getPlanButton: ({ planKey, planDetails }) => (
                                    <PlanButton
                                        planKey={planKey}
                                        planDetails={planDetails}
                                        usage={usage}
                                        handlePlanDowngrade={() =>
                                            setSelectedDowngradePlan(planKey)
                                        }
                                        handleSalesFormClick={handleSalesFormClick}
                                        currentPlan={currentPlan}
                                        frequency={frequency}
                                        isProcessing={isProcessing}
                                        handlePlanPurchase={wrappedHandlePlanPurchase}
                                        isEligibleForTrial={isEligibleForTrial}
                                        isUpgradingTier={
                                            planKey === Plan.UbpCore && isCoreTierBeingUpgraded
                                        }
                                        isDowngradingTier={
                                            planKey === Plan.UbpCore && isCoreTierBeingDowngraded
                                        }
                                        onBundleChange={handleBundleChange}
                                    />
                                )
                            });
                        })}
                </div>

                {/* Small screens */}
                <div className="relative flex w-full flex-1 flex-col items-start divide-y divide-brand overflow-visible rounded-lg border border-brand bg-white md:mt-2 md:flex-row md:items-stretch md:divide-x md:divide-y-0 lg:hidden">
                    {Object.entries(planDataExcludingPublic)
                        .filter(([key]) => key !== Plan.Public)
                        .map(([key, details]) =>
                            renderPlanColumn({
                                planKey: key,
                                planDetails: details,
                                frequency,
                                setFrequency,
                                fsrc,
                                seatAddonSlot: getSeatAddonSlot(key),
                                tierSelectorSlot: getCoreTierSelectorSlot(key),
                                priceOverride: getCorePriceOverride(key),
                                hasLegacyPlan,
                                tierChangeOverride:
                                    key === Plan.UbpCore ? coreTierChangeOverride : null,
                                getPlanButton: ({ planKey, planDetails }) => (
                                    <PlanButton
                                        planKey={planKey}
                                        planDetails={planDetails}
                                        usage={usage}
                                        handlePlanDowngrade={() =>
                                            setSelectedDowngradePlan(planKey)
                                        }
                                        handleSalesFormClick={handleSalesFormClick}
                                        currentPlan={currentPlan}
                                        frequency={frequency}
                                        isProcessing={isProcessing}
                                        handlePlanPurchase={wrappedHandlePlanPurchase}
                                        isEligibleForTrial={isEligibleForTrial}
                                        isUpgradingTier={
                                            planKey === Plan.UbpCore && isCoreTierBeingUpgraded
                                        }
                                        isDowngradingTier={
                                            planKey === Plan.UbpCore && isCoreTierBeingDowngraded
                                        }
                                        onBundleChange={handleBundleChange}
                                    />
                                )
                            })
                        )}
                </div>
            </div>
        );
    };

    if (selectedDowngradePlan) {
        return (
            <CancelPlanController
                usage={usage}
                isOpen={true}
                onClose={() => setSelectedDowngradePlan(null)}
                onCloseParentModal={closeModal}
                selectedDowngradePlan={selectedDowngradePlan}
                selectedFrequency={frequency}
                coreTierCredits={
                    selectedDowngradePlan === Plan.UbpCore ? selectedCoreTier?.credits : null
                }
                fsrc={fsrc}
            />
        );
    }

    return (
        <div className="plansTable">
            <div className="header">
                <h1 className="title">Roboflow Pricing</h1>
                <div className="flex items-center gap-2">
                    <Button type="secondary" onClick={handleSalesFormClick}>
                        Talk to Sales
                    </Button>
                </div>
            </div>

            {isEligibleForTrial && (
                <TrialStartBanner
                    title="Try Roboflow paid features for free"
                    description={`Get started with premium features included with the ${getTrialIncludedPlanNames().join(" and ")} for ${trialDurationLabel}.`}
                    onStartTrial={onActivateTrial}
                    isLoading={isActivatingTrial}
                    className="mt-4"
                />
            )}

            <PlanHighlights
                planData={planData}
                planDataExcludingPublic={planDataExcludingPublic}
                excludePublic={excludePublic}
                columnFrequencyForPlanKey={columnFrequencyForPlanKey}
                setFrequency={setFrequency}
                fsrc={fsrc}
                getSeatAddonSlot={getSeatAddonSlot}
                getCoreTierSelectorSlot={getCoreTierSelectorSlot}
                getCorePriceOverride={getCorePriceOverride}
                hasLegacyPlan={hasLegacyPlan}
                usage={usage}
                setSelectedDowngradePlan={setSelectedDowngradePlan}
                handleSalesFormClick={handleSalesFormClick}
                currentPlan={currentPlan}
                isProcessing={isProcessing}
                wrappedHandlePlanPurchase={wrappedHandlePlanPurchase}
                isEligibleForTrial={isEligibleForTrial}
            />
            <ComparisonTable
                currentPlan={currentPlan}
                frequency={frequency}
                usage={usage}
                excludePublic={excludePublic}
                handleSalesFormClick={handleSalesFormClick}
                fsrc={fsrc}
                selectedCoreTier={
                    hasCoreTierChanged
                        ? selectedCoreTier
                        : isOnACorePlan && subscribedCoreTier
                          ? subscribedCoreTier
                          : !isOnACorePlan
                            ? selectedCoreTier
                            : null
                }
                {...(isOnACorePlan && subscribedCoreTier && !hasCoreTierChanged
                    ? { coreTierInterval: currentPlanFrequency.interval }
                    : {})}
                onFrequencyChange={(interval) => {
                    const newFrequency = frequencies.find((f) => f.interval === interval);
                    setFrequency(newFrequency);
                }}
                getPlanButton={({ planKey, planDetails }) => (
                    <PlanButton
                        planKey={planKey}
                        planDetails={planDetails}
                        usage={usage}
                        handlePlanDowngrade={() =>
                            setSelectedDowngradePlan(planKey as DowngradePlanTargets)
                        }
                        handleSalesFormClick={handleSalesFormClick}
                        currentPlan={currentPlan}
                        frequency={frequency}
                        isProcessing={isProcessing}
                        handlePlanPurchase={wrappedHandlePlanPurchase}
                        isEligibleForTrial={isEligibleForTrial}
                        isUpgradingTier={planKey === Plan.UbpCore && isCoreTierBeingUpgraded}
                        isDowngradingTier={planKey === Plan.UbpCore && isCoreTierBeingDowngraded}
                        onBundleChange={handleBundleChange}
                    />
                )}
            />
        </div>
    );
}
